import fixtureJson from '../tests/fixtures/session-pairs-benchmark.json';
import {
  PAIR_JUDGMENT_LABELS,
  PAIR_JUDGMENTS,
  type CorpusPaper,
  type PairFilter,
  type PairJudgment,
  type PairReview,
  type PairReviewStore,
  type PairSort,
  type SessionPair,
  assessPair,
  dblpUrl,
  doiUrl,
  emptyReviewStore,
  escapeHtml,
  filterPairs,
  formatOverlap,
  paperSnippet,
  parseReviewStore,
  reviewCounts,
  scholarUrl,
  sortPairs,
  uniqueSorted,
} from './pairs-review';

const STORAGE_KEY = 'paper-bidding-session-pair-reviews';

interface SessionPairFixture {
  kind: string;
  corpus: CorpusPaper[];
  pairs: SessionPair[];
}

function requiredElement<T extends Element>(selector: string, isType: (value: Element) => value is T): T {
  const element = document.querySelector(selector);
  if (!element || !isType(element)) {
    throw new Error(`Pair review page is missing ${selector}.`);
  }
  return element;
}

const fixture = fixtureJson as SessionPairFixture;
const papers = new Map(fixture.corpus.map((paper) => [paper.id, paper]));
const pairById = new Map(fixture.pairs.map((pair) => [pair.id, pair]));

const listEl = requiredElement('#pair-list', (el): el is HTMLElement => el instanceof HTMLElement);
const detailEl = requiredElement('#pair-detail', (el): el is HTMLElement => el instanceof HTMLElement);
const statsEl = requiredElement('#review-stats', (el): el is HTMLElement => el instanceof HTMLElement);
const conferenceEl = requiredElement(
  '#filter-conference',
  (el): el is HTMLSelectElement => el instanceof HTMLSelectElement,
);
const yearEl = requiredElement('#filter-year', (el): el is HTMLSelectElement => el instanceof HTMLSelectElement);
const topicEl = requiredElement('#filter-topic', (el): el is HTMLSelectElement => el instanceof HTMLSelectElement);
const tightnessEl = requiredElement(
  '#filter-tightness',
  (el): el is HTMLSelectElement => el instanceof HTMLSelectElement,
);
const judgmentEl = requiredElement(
  '#filter-judgment',
  (el): el is HTMLSelectElement => el instanceof HTMLSelectElement,
);
const queryEl = requiredElement('#filter-query', (el): el is HTMLInputElement => el instanceof HTMLInputElement);
const qualityEl = requiredElement(
  '#filter-quality',
  (el): el is HTMLSelectElement => el instanceof HTMLSelectElement,
);
const sortEl = requiredElement('#filter-sort', (el): el is HTMLSelectElement => el instanceof HTMLSelectElement);
const nextUnreviewedEl = requiredElement(
  '#next-unreviewed',
  (el): el is HTMLButtonElement => el instanceof HTMLButtonElement,
);
const exportEl = requiredElement('#export-reviews', (el): el is HTMLButtonElement => el instanceof HTMLButtonElement);
const importEl = requiredElement('#import-reviews', (el): el is HTMLInputElement => el instanceof HTMLInputElement);

const store = loadStore();
const noteDrafts = new Map<string, string>();
let selectedId = pairIdFromHash() ?? fixture.pairs[0]?.id ?? '';
let swapped = false;
let showFullAbstracts = false;

fillSelect(
  conferenceEl,
  uniqueSorted(fixture.pairs.map((pair) => pair.conference)),
  'All conferences',
);
fillSelect(
  yearEl,
  uniqueSorted(fixture.pairs.map((pair) => String(pair.year))),
  'All years',
);
fillSelect(topicEl, uniqueSorted(fixture.pairs.map((pair) => pair.topic)), 'All topics');

function currentFilter(): PairFilter {
  const tightness = tightnessEl.value;
  const judgment = judgmentEl.value;
  const quality = qualityEl.value;
  return {
    conference: conferenceEl.value,
    year: yearEl.value,
    topic: topicEl.value,
    tightness: tightness === 'tight' || tightness === 'loose' ? tightness : 'all',
    judgment:
      judgment === 'unreviewed' ||
      judgment === 'good' ||
      judgment === 'weak' ||
      judgment === 'unrelated' ||
      judgment === 'unsure'
        ? judgment
        : 'all',
    quality: quality === 'noisy' || quality === 'clones' ? quality : 'all',
    query: queryEl.value,
  };
}

function currentSort(): PairSort {
  const sort = sortEl.value;
  if (sort === 'relatedness-asc' || sort === 'relatedness-desc' || sort === 'fixture') return sort;
  return 'attention';
}

function visiblePairs(): SessionPair[] {
  return sortPairs(
    filterPairs(fixture.pairs, papers, store.reviews, currentFilter()),
    papers,
    store.reviews,
    currentSort(),
  );
}

function selectPair(pairId: string): void {
  const visible = visiblePairs();
  const nextId = visible.some((pair) => pair.id === pairId) ? pairId : (visible[0]?.id ?? '');
  if (nextId !== selectedId) {
    swapped = false;
    showFullAbstracts = false;
  }
  selectedId = nextId;
  writeHash(selectedId);
  render();
}

function pairIdFromHash(): string | undefined {
  const raw = decodeURIComponent(location.hash.replace(/^#/, ''));
  return raw && pairById.has(raw) ? raw : undefined;
}

function writeHash(pairId: string): void {
  const next = pairId ? `#${encodeURIComponent(pairId)}` : '';
  if (next !== location.hash) history.replaceState(null, '', next || location.pathname);
}

function jumpUnreviewed(): void {
  const visible = visiblePairs();
  const current = visible.findIndex((pair) => pair.id === selectedId);
  const later = visible.slice(current + 1).find((pair) => !store.reviews[pair.id]);
  const earlier = visible.find((pair) => !store.reviews[pair.id]);
  const next = later ?? earlier;
  if (next) selectPair(next.id);
}

function moveSelection(delta: number): void {
  const visible = visiblePairs();
  const index = visible.findIndex((pair) => pair.id === selectedId);
  const next = visible[Math.min(visible.length - 1, Math.max(0, index + delta))];
  if (next) selectPair(next.id);
}

function setJudgment(pairId: string, judgment: PairJudgment): void {
  const existing = store.reviews[pairId];
  store.reviews[pairId] = {
    judgment,
    note: noteDrafts.get(pairId) ?? existing?.note ?? '',
    updatedAt: new Date().toISOString(),
  };
  saveStore(store);
  render();
}

function setNote(pairId: string, note: string): void {
  noteDrafts.set(pairId, note);
  const existing = store.reviews[pairId];
  if (!existing) return;
  existing.note = note;
  existing.updatedAt = new Date().toISOString();
  saveStore(store);
}

function render(): void {
  renderStats();
  const visible = visiblePairs();
  if (!visible.some((pair) => pair.id === selectedId)) {
    selectedId = visible[0]?.id ?? '';
  }
  writeHash(selectedId);
  listEl.innerHTML = visible.length
    ? visible.map((pair) => listItemHtml(pair, store.reviews[pair.id], pair.id === selectedId)).join('')
    : '<p class="empty-state">No pairs match these filters.</p>';
  const selected = pairById.get(selectedId);
  detailEl.innerHTML = selected ? detailHtml(selected, store.reviews[selected.id]) : '';
  document.querySelector(`[data-pair-id="${CSS.escape(selectedId)}"]`)?.scrollIntoView({
    block: 'nearest',
  });
}

function renderStats(): void {
  const counts = reviewCounts(fixture.pairs, store.reviews);
  const reviewed = counts.total - counts.unreviewed;
  statsEl.innerHTML = [
    statHtml(String(counts.total), 'Pairs'),
    statHtml(`${reviewed}/${counts.total}`, 'Reviewed'),
    statHtml(String(counts.good), 'Good'),
    statHtml(String(counts.weak), 'Weak'),
    statHtml(String(counts.unrelated), 'Unrelated'),
    statHtml(String(counts.unsure), 'Unsure'),
  ].join('');
}

function listItemHtml(pair: SessionPair, review: PairReview | undefined, current: boolean): string {
  const preference = papers.get(pair.preferenceId);
  const expertise = papers.get(pair.expertiseId);
  const judgment = review?.judgment ?? 'unreviewed';
  const label = review ? PAIR_JUDGMENT_LABELS[review.judgment] : 'Unreviewed';
  const assessment =
    expertise && preference ? assessPair(pair, expertise, preference) : undefined;
  const flag = assessment?.flags[0];
  return `<button class="pair-list-item" type="button" data-pair-id="${escapeHtml(pair.id)}" aria-current="${current ? 'true' : 'false'}">
    <span class="judgment-pill ${judgment}">${escapeHtml(label)}</span>
    ${flag ? `<span class="judgment-pill ${flag === 'title-clone' ? 'unsure' : 'unrelated'}">${escapeHtml(flag.replaceAll('-', ' '))}</span>` : ''}
    <span class="list-title">${escapeHtml(preference?.title ?? pair.preferenceId)}</span>
    <small>${escapeHtml(pair.conference)} ${pair.year} · overlap ${assessment ? formatOverlap(assessment.titleOverlap) : '—'} · ${escapeHtml(pair.session)}</small>
  </button>`;
}

function detailHtml(pair: SessionPair, review: PairReview | undefined): string {
  const expertise = papers.get(pair.expertiseId);
  const preference = papers.get(pair.preferenceId);
  if (!expertise || !preference) {
    return '<p class="empty-state">This pair is missing corpus papers.</p>';
  }
  const assessment = assessPair(pair, expertise, preference);
  const left = swapped ? preference : expertise;
  const right = swapped ? expertise : preference;
  const leftover = pair.sameSessionIds
    .map((paperId) => papers.get(paperId))
    .filter((paper): paper is CorpusPaper => Boolean(paper));
  const negatives = pair.hardNegativeIds
    .map((paperId) => papers.get(paperId))
    .filter((paper): paper is CorpusPaper => Boolean(paper));
  const visible = visiblePairs();
  const position = visible.findIndex((item) => item.id === pair.id) + 1;
  return `
    <div class="pair-meta">
      <span class="meta-chip">${position} of ${visible.length}</span>
      <span class="meta-chip">${escapeHtml(pair.conference)} ${pair.year}</span>
      <span class="meta-chip">${escapeHtml(pair.session)}</span>
      <span class="meta-chip">${escapeHtml(pair.topic)}</span>
      <span class="meta-chip">${pair.sessionTight ? 'Tight session' : 'Looser session'}</span>
      <span class="meta-chip">Title overlap ${formatOverlap(assessment.titleOverlap)}</span>
      <span class="meta-chip">Abstract overlap ${formatOverlap(assessment.abstractOverlap)}</span>
      <span class="meta-chip">Relatedness ${assessment.relatedness.toFixed(2)}</span>
      ${assessment.flags.map((flag) => `<span class="meta-chip warn">${escapeHtml(flag.replaceAll('-', ' '))}</span>`).join('')}
      <div class="pair-nav">
        <button class="button ghost" type="button" data-move="-1">Previous</button>
        <button class="button ghost" type="button" data-move="1">Next</button>
        <button class="button ghost" type="button" data-swap="true" aria-pressed="${swapped ? 'true' : 'false'}">Swap roles</button>
        <button class="button ghost" type="button" data-expand="true" aria-pressed="${showFullAbstracts ? 'true' : 'false'}">${showFullAbstracts ? 'Collapse abstracts' : 'Full abstracts'}</button>
      </div>
    </div>
    <p class="keep-reason">${escapeHtml(assessment.keepReason)}</p>
    <div class="paper-compare">
      ${paperCardHtml(swapped ? 'Expertise (swapped)' : 'Expertise (query)', left)}
      ${paperCardHtml(swapped ? 'Review preference (swapped)' : 'Review preference (relevant)', right)}
    </div>
    <div class="related-grid">
      <div>
        <h2>Same-session leftovers (${leftover.length})</h2>
        ${leftover.length ? leftover.map((paper) => relatedHtml(paper, 'Related, not required')).join('') : '<p class="empty-state">No leftover papers in this session.</p>'}
      </div>
      <div>
        <h2>Hard negatives (${negatives.length})</h2>
        ${negatives.length ? negatives.map((paper) => relatedHtml(paper, paper.topic)).join('') : '<p class="empty-state">No neighboring-topic negatives for this pair.</p>'}
      </div>
    </div>
    <div class="judgment-panel">
      <h2>If you wrote the expertise paper, would you want to review the preference paper?</h2>
      <div class="judgment-actions">
        ${PAIR_JUDGMENTS.map(
          (judgment) =>
            `<button class="button ${judgment === 'good' ? 'primary' : 'secondary'}" type="button" data-judgment="${judgment}" aria-pressed="${review?.judgment === judgment ? 'true' : 'false'}">${PAIR_JUDGMENT_LABELS[judgment]}</button>`,
        ).join('')}
      </div>
      <label>
        Notes
        <textarea id="pair-note" placeholder="Why this pair is a good or bad bidding label">${escapeHtml(noteDrafts.get(pair.id) ?? review?.note ?? '')}</textarea>
      </label>
      <p class="kbd-hint">j/k move · n next unreviewed · 1 good · 2 weak · 3 unrelated · 4 unsure · s swap · f full abstracts. Labels stay in this browser until you export them.</p>
    </div>
  `;
}

function paperCardHtml(role: string, paper: CorpusPaper): string {
  const snippet = paperSnippet(paper.abstract, showFullAbstracts ? 20000 : 900);
  return `<article class="pair-paper-card">
    <h2>${escapeHtml(role)}</h2>
    <h3>${escapeHtml(paper.title)}</h3>
    <p class="authors">${escapeHtml(paper.authors || 'Authors not listed')}</p>
    <p class="paper-links">
      <a href="${escapeHtml(paper.sourceUrl)}" target="_blank" rel="noreferrer">${escapeHtml(paper.conference)} ${paper.year} source</a>
      ${paper.doi ? `· <a href="${escapeHtml(doiUrl(paper.doi))}" target="_blank" rel="noreferrer">DOI</a>` : ''}
      · <a href="${escapeHtml(scholarUrl(paper.title))}" target="_blank" rel="noreferrer">Scholar</a>
      · <a href="${escapeHtml(dblpUrl(paper.title))}" target="_blank" rel="noreferrer">DBLP</a>
    </p>
    <p class="abstract">${escapeHtml(snippet.text)}</p>
  </article>`;
}

function relatedHtml(paper: CorpusPaper, badge: string): string {
  const snippet = paperSnippet(paper.abstract, 220);
  return `<article class="related-item">
    <span class="judgment-pill unreviewed">${escapeHtml(badge)}</span>
    <strong>${escapeHtml(paper.title)}</strong>
    <small>${escapeHtml(paper.authors)}</small>
    <p>${escapeHtml(snippet.text)}</p>
  </article>`;
}

function statHtml(value: string, label: string): string {
  return `<div class="review-stat"><strong>${escapeHtml(value)}</strong><span>${escapeHtml(label)}</span></div>`;
}

function fillSelect(select: HTMLSelectElement, values: readonly string[], allLabel: string): void {
  select.innerHTML = [`<option value="">${escapeHtml(allLabel)}</option>`]
    .concat(values.map((value) => `<option value="${escapeHtml(value)}">${escapeHtml(value)}</option>`))
    .join('');
}

function loadStore(): PairReviewStore {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? parseReviewStore(raw) : emptyReviewStore();
  } catch {
    return emptyReviewStore();
  }
}

function saveStore(value: PairReviewStore): void {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(value));
}

listEl.addEventListener('click', (event) => {
  const target = event.target;
  if (!(target instanceof Element)) return;
  const button = target.closest('[data-pair-id]');
  if (button instanceof HTMLElement && button.dataset.pairId) selectPair(button.dataset.pairId);
});

detailEl.addEventListener('click', (event) => {
  const target = event.target;
  if (!(target instanceof Element)) return;
  const mover = target.closest('[data-move]');
  if (mover instanceof HTMLElement && mover.dataset.move) {
    moveSelection(Number(mover.dataset.move));
    return;
  }
  const judgmentButton = target.closest('[data-judgment]');
  if (judgmentButton instanceof HTMLElement && judgmentButton.dataset.judgment) {
    const judgment = judgmentButton.dataset.judgment;
    if (
      judgment === 'good' ||
      judgment === 'weak' ||
      judgment === 'unrelated' ||
      judgment === 'unsure'
    ) {
      setJudgment(selectedId, judgment);
    }
    return;
  }
  if (target.closest('[data-swap]')) {
    swapped = !swapped;
    render();
    return;
  }
  if (target.closest('[data-expand]')) {
    showFullAbstracts = !showFullAbstracts;
    render();
  }
});

detailEl.addEventListener('input', (event) => {
  const target = event.target;
  if (target instanceof HTMLTextAreaElement && target.id === 'pair-note' && selectedId) {
    setNote(selectedId, target.value);
  }
});

for (const control of [conferenceEl, yearEl, topicEl, tightnessEl, judgmentEl, qualityEl, sortEl]) {
  control.addEventListener('change', render);
}
queryEl.addEventListener('input', render);
nextUnreviewedEl.addEventListener('click', jumpUnreviewed);
window.addEventListener('hashchange', () => {
  const fromHash = pairIdFromHash();
  if (fromHash && fromHash !== selectedId) selectPair(fromHash);
});

exportEl.addEventListener('click', () => {
  const blob = new Blob([JSON.stringify(store, null, 2)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = 'session-pair-reviews.json';
  link.click();
  URL.revokeObjectURL(url);
});

importEl.addEventListener('change', async () => {
  const file = importEl.files?.[0];
  if (!file) return;
  const imported = parseReviewStore(await file.text());
  store.reviews = { ...store.reviews, ...imported.reviews };
  saveStore(store);
  importEl.value = '';
  render();
});

document.addEventListener('keydown', (event) => {
  const target = event.target;
  if (target instanceof HTMLInputElement || target instanceof HTMLTextAreaElement || target instanceof HTMLSelectElement) {
    return;
  }
  if (event.key === 'j' || event.key === 'ArrowDown' || event.key === 'ArrowRight') {
    event.preventDefault();
    moveSelection(1);
  } else if (event.key === 'k' || event.key === 'ArrowUp' || event.key === 'ArrowLeft') {
    event.preventDefault();
    moveSelection(-1);
  } else if (event.key === '1') setJudgment(selectedId, 'good');
  else if (event.key === '2') setJudgment(selectedId, 'weak');
  else if (event.key === '3') setJudgment(selectedId, 'unrelated');
  else if (event.key === '4') setJudgment(selectedId, 'unsure');
  else if (event.key === 'n') {
    event.preventDefault();
    jumpUnreviewed();
  } else if (event.key === 's') {
    swapped = !swapped;
    render();
  } else if (event.key === 'f') {
    showFullAbstracts = !showFullAbstracts;
    render();
  }
});

render();
